import { useState } from "react";
import { Pencil, Loader2, Save, Search, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export function EditShareholderDialog({ companyId, shareholder, classes, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [holdings, setHoldings] = useState({});
  const [holderType, setHolderType] = useState("person");
  const [idNumber, setIdNumber] = useState("");
  const [name, setName] = useState("");
  const [nameFromProff, setNameFromProff] = useState(false);
  const [proffLoading, setProffLoading] = useState(false);
  const [lastProffOrg, setLastProffOrg] = useState("");

  const classNames = (classes && classes.length ? classes.map((c) => c.name) : Object.keys(shareholder.holdings || {}));

  const openDialog = () => {
    const init = {};
    classNames.forEach((n) => { init[n] = String(shareholder.holdings?.[n] ?? 0); });
    setHoldings(init);
    setHolderType(shareholder.holder_type === "selskap" ? "selskap" : "person");
    setIdNumber(shareholder.id_number || "");
    setName(shareholder.name || "");
    setNameFromProff(false);
    setLastProffOrg("");
    setOpen(true);
  };

  // Hent firmanavn automatisk fra Proff når org.nr er komplett (9 siffer) for et selskap.
  const fetchProffName = async (org) => {
    setProffLoading(true);
    setLastProffOrg(org);
    try {
      const { data } = await api.get(`/proff/company/${org}`);
      if (data?.name) {
        setName(data.name);
        setNameFromProff(true);
      }
    } catch {
      // stille – bruker kan skrive navn manuelt
    } finally {
      setProffLoading(false);
    }
  };

  const onOrgChange = (val) => {
    setIdNumber(val);
    setNameFromProff(false);
    const d = val.replace(/\D/g, "");
    if (holderType === "selskap" && d.length === 9 && d !== lastProffOrg) {
      fetchProffName(d);
    }
  };

  const save = async () => {
    const digits = idNumber.replace(/\D/g, "");
    if (idNumber.trim()) {
      if (holderType === "selskap" && digits.length !== 9) {
        toast.error("Organisasjonsnummer må være 9 siffer");
        return;
      }
      if (holderType !== "selskap" && digits.length !== 11) {
        toast.error("Fødselsnummer må være 11 siffer");
        return;
      }
    }
    const holdingsPayload = {};
    for (const [k, v] of Object.entries(holdings)) {
      const n = Number(v);
      if (Number.isNaN(n) || n < 0) {
        toast.error("Skriv inn gyldige tall (0 eller mer)");
        return;
      }
      holdingsPayload[k] = Math.round(n);
    }
    setSaving(true);
    try {
      await api.put(`/companies/${companyId}/shareholders/${shareholder.id}`, {
        holdings: holdingsPayload,
        holder_type: holderType,
        id_number: idNumber.trim(),
        name: name.trim(),
      });
      toast.success("Aksjonær oppdatert");
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const isCompany = holderType === "selskap";
  const idDigits = idNumber.replace(/\D/g, "");
  const idError = idNumber.trim() && ((isCompany && idDigits.length !== 9) || (!isCompany && idDigits.length !== 11))
    ? (isCompany ? "Organisasjonsnummer må være 9 siffer" : "Fødselsnummer må være 11 siffer")
    : "";

  return (
    <>
      <button
        data-testid={`edit-shareholder-${shareholder.id}`}
        onClick={openDialog}
        className="text-muted-foreground transition-colors hover:text-primary"
        title="Rediger aksjonær"
      >
        <Pencil className="h-3.5 w-3.5" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="edit-shareholder-dialog">
          <DialogHeader>
            <DialogTitle>Rediger aksjonær – {shareholder.name}</DialogTitle>
            <DialogDescription>
              Endre type (person/bedrift), identifikasjon og antall aksjer. Justering av aksjer oppretter ingen transaksjon.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-1">
            <div>
              <Label className="text-xs">Type</Label>
              <div className="mt-1.5 flex gap-1.5 rounded-lg border border-border p-1">
                {[["person", "Person"], ["selskap", "Bedrift"]].map(([v, l]) => (
                  <button
                    key={v}
                    type="button"
                    data-testid={`edit-holdertype-${v}`}
                    onClick={() => setHolderType(v)}
                    className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${holderType === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs">{isCompany ? "Org.nr" : "Fødselsnummer"}</Label>
              <Input
                data-testid="edit-idnumber"
                className={`mt-1.5 ${idError ? "border-destructive focus-visible:ring-destructive" : ""}`}
                inputMode="numeric"
                value={idNumber}
                onChange={(e) => onOrgChange(e.target.value)}
                placeholder={isCompany ? "9 siffer" : "11 siffer"}
              />
              {idError ? (
                <p className="mt-1 text-xs text-destructive" data-testid="edit-idnumber-error">{idError}</p>
              ) : null}
            </div>

            <div>
              <Label className="text-xs">Navn</Label>
              <div className="mt-1.5 flex gap-2">
                <Input
                  data-testid="edit-name"
                  value={name}
                  onChange={(e) => { setName(e.target.value); setNameFromProff(false); }}
                  placeholder={isCompany ? "Firmanavn (hentes fra Proff)" : "Navn"}
                />
                {isCompany ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="shrink-0 gap-1.5"
                    data-testid="edit-proff-lookup"
                    disabled={proffLoading}
                    onClick={() => {
                      const d = idNumber.replace(/\D/g, "");
                      if (d.length === 9) fetchProffName(d);
                      else toast.error("Skriv inn org.nr (9 siffer) først");
                    }}
                  >
                    {proffLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Hent
                  </Button>
                ) : null}
              </div>
              {nameFromProff ? (
                <p className="mt-1 flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400" data-testid="edit-name-verified">
                  <Check className="h-3.5 w-3.5" /> Bekreftet mot Proff
                </p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label className="text-xs">Aksjer per klasse</Label>
              {classNames.map((n) => (
                <div key={n} className="flex items-center justify-between gap-4">
                  <span className="text-sm text-muted-foreground">{n}</span>
                  <Input
                    data-testid={`edit-shares-${n}`}
                    type="number"
                    inputMode="numeric"
                    className="h-9 w-40 text-right"
                    value={holdings[n] ?? ""}
                    onChange={(e) => setHoldings({ ...holdings, [n]: e.target.value })}
                  />
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Avbryt</Button>
            <Button className="gap-2" data-testid="edit-shareholder-save" onClick={save} disabled={saving || !!idError}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Lagre
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
